import {SimpleCache} from "./simple-cache";

export enum Env {
    staging = "staging",
    prod = "prod"
}

export interface IConfig {
    env: Env
    merchant_id: string
    publishable_key: string
    checkout_link_host: string
    force_hosted_checkout_redirect: boolean
}

export interface IPDPInfo {
    productId: string
    variantId: string
    quantity: number
}

// https://github.com/BoltApp/source/blob/master/hail/bolt/tipser/types/types.go#L13
export interface ITCSProduct {
    merchantProductId: string
    variants: ITCSVariant[]
}

// https://github.com/BoltApp/source/blob/master/hail/bolt/tipser/types/types.go#L61
export interface ITCSVariant {
    merchantVariantId: string
    stockCount: number
    stockCountIsInfinite: boolean
}

// https://github.com/BoltApp/source/blob/master/hail/bolt/serviceclients/subscriptionsapi/inputs/requests.go#L26
export interface ISubscriptionPlan {
    id: string
    name: string
    frequency: number
    frequency_unit: string
}

// web-pixels-manager-setup has an embedded JSON, it has more fields, but we only define the ones we use
export interface IEmbeddedProduct {
    product: {
        id: string
    },
    id: string
}

export interface IEmbeddedCart {
    merchandise: IEmbeddedProduct
}

const TcsConfig = {
    staging: {
        baseUrl: "https://api.dev.tipser.com",
        posId: "65087023a109749e7cd18b62"
    },
    prod: {
        baseUrl: "https://api.tipser.com",
        posId: "616839b0a171c90438167d1d"
    }
};

const BoltConfig = {
    staging: {
        baseUrl: 'https://api-staging.bolt.com',
    },
    prod: {
        baseUrl: 'https://api.bolt.com',
    }
};

export class BoltButton {
    iframe: HTMLIFrameElement | null = null;
    isMounted: boolean = false;
    subscription_plan_id: string = "";
    quantityCache: SimpleCache<string, number>;
    plansCache: SimpleCache<string, ISubscriptionPlan[]>;
    config: IConfig;

    // Initialized by installContainer
    container!: HTMLDivElement;
    window!: HTMLDivElement;
    windowClose!: HTMLDivElement;

    constructor(publishableKey: string, checkoutLinkHost: string, merchantId: string, env: Env, redirect: boolean = false) {
        this.iframe = null;
        this.isMounted = false;
        this.subscription_plan_id = "";
        this.config = {
            env: env,
            merchant_id: merchantId,
            publishable_key: publishableKey,
            checkout_link_host: checkoutLinkHost,
            force_hosted_checkout_redirect: redirect,
        };
        this.quantityCache = new SimpleCache();
        this.plansCache = new SimpleCache();
    }

    installContainer() {
        const container = document.getElementById(
            "bolt-express-checkout_container"
        );
        if (container) {
            this.container = <HTMLDivElement>container;
            return;
        }
        const divWithId = (id: string) => {
            const el = document.createElement("div");
            el.id = id;
            return el;
        };
        this.container = divWithId("bolt-express-checkout_container");
        this.container.appendChild(divWithId("bolt-express-checkout_overlay"));

        this.windowClose = divWithId("bolt-express-checkout_window-close");
        this.container.appendChild(this.windowClose);

        this.window = divWithId("bolt-express-checkout_window");
        this.container.appendChild(this.window);

        document.body.appendChild(this.container);

        this.windowClose.addEventListener(
            "click",
            this.handleWindowClose.bind(this)
        );
    }

    async installSubscriptionPlanSelect(select: HTMLSelectElement) {
        this.populateSubscriptionPlans(select);
        select.addEventListener("change", this.handleSubscriptionPlanChange.bind(this));

        const productForm = document.querySelector<HTMLFormElement>("form[data-type=add-to-cart-form]");
        if (productForm === null) {
            throw new Error("Add to cart form not found");
        }
        productForm.addEventListener("change", this.populateSubscriptionPlans.bind(this, select));
    }

    installCartButton(btn: HTMLElement) {
        btn.addEventListener("click", this.handleCartClick.bind(this));
    }

    installBuyNowButton(btn: HTMLElement) {
        btn.addEventListener("click", this.handlePDPClick.bind(this, false));
    }

    installSubscribeButton(btn: HTMLElement) {
        btn.addEventListener("click", this.handlePDPClick.bind(this, true));
    }

    async populateSubscriptionPlans(select: HTMLSelectElement) {
        const pdpInfo = this.getPdpInfo();
        const plans = await this.getAvailablePlans(pdpInfo.productId, pdpInfo.variantId);
        if (plans.length == 0) {
            select.innerHTML = '<option value="">No plans available</option>';
            return;
        }
        select.innerHTML = '<option value="">Select a plan</option>';
        for (const plan of plans) {
            const option = document.createElement("option");
            option.value = plan.id;
            option.textContent = `${plan.name} - ${plan.frequency} ${plan.frequency_unit}`;
            select.appendChild(option);
        }
    }

    async handleSubscriptionPlanChange(event: Event) {
        this.subscription_plan_id = (<HTMLOptionElement>event.target).value;
    }

    async handlePDPClick(subscription: boolean) {
        if (!this.isMounted) {
            const url = this.getCheckoutLinkBase();

            if (subscription) {
                if (!this.subscription_plan_id) {
                    this.showPdpError("You must select a plan first");
                    return;
                }
                url.searchParams.set("subscription_plan_id", this.subscription_plan_id);
            }

            const pdpInfo = this.getPdpInfo();
            if (!(await this.validQuantity(pdpInfo))) {
                return;
            }

            url.searchParams.set("merchant_product_id", pdpInfo.productId);
            url.searchParams.set("merchant_variant_id", pdpInfo.variantId);
            url.searchParams.set("quantity", pdpInfo.quantity.toString());

            this.openCheckout(url);
        }
    }

    async handleCartClick(event: MouseEvent) {
        if (!this.isMounted) {
            const url = this.getCheckoutLinkBase();

            // this is shady and very much depends on the shape of this embedded JS which tends to change for different reasons, works well for now but is not fully reliable
            // we might want to use a different method but variant id/product id pairs don't exist anywhere else in the DOM.
            // one way could be to use GET /cart request which Shopify FE is already making requests to - just add Accept: application/json for perfect JSON response but this will add extra latency

            const webPixel = document.getElementById("web-pixels-manager-setup");
            if (webPixel === null) {
                throw new Error("web-pixels-manager-setup not found");
            }
            const input = webPixel.innerText;
            const hasCartObject = input.indexOf('"lines":') > -1;

            const begin = input.indexOf('"productVariants":') + '"productVariants":'.length;
            const end = input.indexOf("]", begin) + 1;
            const variantsInput: IEmbeddedProduct[] = JSON.parse(input.slice(begin, end));
            const items = variantsInput.map((item) => ({
                product_id: item.product.id,
                variant_id: item.id
            }))

            // this handle the case when adding to cart from collection view directly
            if (input.indexOf('"productVariants":', end) > -1) {
                const collectionBegin = input.indexOf('"productVariants":', end) + '"productVariants":'.length;
                const collectionEnd = input.indexOf("]", collectionBegin) + 1;

                const variantsCollection: IEmbeddedProduct[] = JSON.parse(input.slice(collectionBegin, collectionEnd));
                const collectionItems = variantsCollection.map((item) => ({
                    product_id: item.product.id,
                    variant_id: item.id
                }))

                // list must be unique and in some cases we will have the same variant in both the cart and productVariants
                items.push(...collectionItems.filter(collectionItem => items.every(i => i.variant_id != collectionItem.variant_id)));
            }

            if (hasCartObject) {
                const begin = input.indexOf('"lines":') + '"lines":'.length;
                const end = input.indexOf("}],", begin) + 2;

                const cartInput: IEmbeddedCart[] = JSON.parse(input.slice(begin, end));
                const cartItems = cartInput.map((item) => ({
                    product_id: item.merchandise.product.id,
                    variant_id: item.merchandise.id
                }));

                // list must be unique and in some cases we will have the same variant in both the cart and productVariants
                items.push(...cartItems.filter(cartItem => items.every(i => i.variant_id != cartItem.variant_id)));
            }

            const mode = (<HTMLButtonElement>event.currentTarget).dataset.boltBuyMode;
            const qtySelectorPrefix = mode === "cart" ? "cart-items" : "cart-drawer-items";

            const cartItems = items.map((item) => ({
                ...item,
                qtyElement: document.querySelector<HTMLInputElement>(
                    `${qtySelectorPrefix} [data-quantity-variant-id="${item.variant_id}"]`
                )
            }))
                // after removing item from the cart this will be null
                .filter(x => x.qtyElement !== null)
                .map(item => ({
                    ...item,
                    qty: item.qtyElement?.dataset.cartQuantity!,
                    index: parseInt(item.qtyElement?.dataset.index!) // for stable ordering
                }))
                .sort((a, b) => a.index - b.index);

            url.searchParams.set(
                "merchant_product_id",
                cartItems.map((x) => x.product_id).join(",")
            );
            url.searchParams.set(
                "merchant_variant_id",
                cartItems.map((x) => x.variant_id).join(",")
            );
            url.searchParams.set(
                "quantity",
                cartItems.map((x) => x.qty).join(",")
            );

            this.openCheckout(url);
        }
    }

    getCheckoutLinkBase() {
        const url = new URL("/g", 'https://' + this.config.checkout_link_host);
        url.searchParams.set("publishable_key", this.config.publishable_key);

        return url;
    }

    async validQuantity(pdpInfo: IPDPInfo) {
        if (pdpInfo.quantity < 1) {
            this.showPdpError("Quantity must be greater than 0");
            return false;
        }

        const qty = await this.getQuantityAvailable(pdpInfo.productId, pdpInfo.variantId);

        if (pdpInfo.quantity > qty) {
            if (qty === 1) {
                this.showPdpError("Sorry, there is only 1 item available");
            } else {
                this.showPdpError(`Sorry, there are only ${qty} items available`);
            }
            return false;
        }
        return true;
    }

    openCheckout(url: URL) {
        if (this.config.force_hosted_checkout_redirect) {
            window.location.href = url.href;
        } else {
            this.iframe = this.createBoltExpressIframe(url.href);
            this.mount(this.window, this.iframe);
        }
    }

    showPdpError(message: string) {
        const errorWrapper = document.querySelector(".product-form__error-message-wrapper");
        if (!errorWrapper) {
            console.group("Bolt Checkout Error");
            console.error("Error message wrapper not found");
            console.error("Error message:", message);
            console.groupEnd();
            return;
        }
        errorWrapper.querySelector("span")!.innerText = message;
        errorWrapper.removeAttribute("hidden");
    }

    async getAvailablePlans(productId: string, variantId: string) {
        const cacheKey = `${productId}_${variantId}`;
        return this.plansCache.get(cacheKey, this.fetchPlans(productId, variantId), 3_600_000);
    }

    async fetchPlans(productId: string, variantId: string): Promise<ISubscriptionPlan[]> {
        const plansURL = new URL("/v1/subscriptions/plans", BoltConfig[this.config.env].baseUrl);
        const headers = {
            "x-publishable-key": this.config.publishable_key,
        };

        plansURL.searchParams.set("merchant_product_id", productId);
        plansURL.searchParams.set("merchant_variant_id", variantId);

        const res = await fetch(plansURL, { headers });
        if (!res.ok) {
            console.error("Error loading plans:", res.text());
            return [];
        }
        return await res.json();
    }

    async fetchQuantity(productId: string, variantId: string): Promise<number> {
        const tcs = TcsConfig[this.config.env];
        const url = new URL("/v6/products", tcs.baseUrl);
        url.searchParams.set("merchantId", this.config.merchant_id)
        url.searchParams.set("posId", tcs.posId);
        url.searchParams.set("merchantProductIds", productId);

        const response = await fetch(url);

        if (!response.ok) {
            console.error("Failed to fetch TCS product data");
            return 0;
        }

        const data: ITCSProduct[] = await response.json();

        const product = data.find((p) => p.merchantProductId === productId);
        if (!product) {
            console.error("Product not found in TCS data");
            return 0;
        }

        const variant = product.variants.find((v) => v.merchantVariantId === variantId);
        if (!variant) {
            console.error("Variant not found in TCS data");
            return 0;
        }

        if (variant.stockCountIsInfinite) {
            return Infinity;
        }

        return variant.stockCount;
    }

    async getQuantityAvailable(productId: string, variantId: string) {
        const cacheKey = `${productId}_${variantId}`;
        return this.quantityCache.get(cacheKey, this.fetchQuantity(productId, variantId), 60_000);
    }

    getPdpInfo(): IPDPInfo {
        // favor quick add modal ("Choose option") first
        const form = document.querySelector<HTMLFormElement>("quick-add-modal[open] form[data-type=add-to-cart-form]") ??
            document.querySelector<HTMLFormElement>("form[data-type=add-to-cart-form]");
        if (form === null) {
            throw new Error("PDP Info form not found");
        }
        const formData = new FormData(form);

        return {
            productId: <string>formData.get("product-id"),
            variantId: <string>formData.get("id"),
            quantity: Number(formData.get('quantity')) ?? 1
        };
    }

    handleWindowClose() {
        if (this.isMounted) {
            this.iframeOnDestroy();
            setTimeout(() => {
                if (this.iframe !== null) {
                    this.unmount(this.window, this.iframe);
                    this.iframe = null;
                }
            }, 310);
        }
    }

    createBoltExpressIframe(url: string) {
        const iframe = document.createElement("iframe");
        iframe.src = url;
        iframe.id = "bolt-express-checkout_iframe";
        iframe.onload = this.iframeOnLoad.bind(this);
        return iframe;
    }

    iframeOnLoad() {
        this.container.classList.add("visible");
    }

    iframeOnDestroy() {
        this.container.classList.remove("visible");
    }

    mount(parent: HTMLElement, child: HTMLIFrameElement) {
        this.isMounted = true;
        parent.replaceChildren(child);
    }

    unmount(parent: HTMLElement, child: HTMLIFrameElement) {
        parent.removeChild(child);
        this.isMounted = false;
    }
}
